import firebase from "firebase/app";

import { Divider } from "semantic-ui-react";

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { db } from "astrid-firebase";
import notify from "astrid-firebase/src/notifications";
import updateDiscussionChannel from "astrid-firestore/src/api/discussion/updateDiscussionChannel";
import updateDocument from "astrid-firestore/src/helpers/updateDocument";
import useDocumentData from "astrid-firestore/src/hooks/useDocumentData";

import { useProfile } from "../../../../../authentication/state/profile";
import useHasRight from "../../../../../authorization/hooks/useHasRight";
import { useOrganizationId } from "../../../../../organizations/state/organizationId";
import LoadingContext from "../../../../../ui/components/LoadingContext/LoadingContext";
import { setDiscussion } from "../../../../state/discussion";

import DiscussionThread from "../../../Discussion/DiscussionThread";

import DiscussionChannelHeader from "./DiscussionChannelHeader";

export default function DiscussionChannel({ channelId }) {
	const { t } = useTranslation();
	const user = useProfile();
	const hasRight = useHasRight();
	const orgId = useOrganizationId();

	const [channel, loading, error] = useDocumentData(db.collection("discussionChannels").doc(channelId));

	const onCommentCreated = async (comment) => {
		const users = channel.users.find(({ id }) => id === user.id) ? channel.users : [...channel.users, user];
		const userIds = users.map(({ id }) => id);

		await updateDiscussionChannel(firebase, channel.ref, {
			seenByUserIds: [user.id],
			userIds,
			users,
			lastComment: comment,
			needsAttention: hasRight("discussions.needsAttention"),
		});

		const notifyUserIds = userIds.filter((id) => id !== user.id);

		await notify(notifyUserIds, (user) => {
			const lang = user.languageCode || "en";

			return {
				subject: t("unreadMessageSubject", "You have an unread message", {
					lng: lang,
				}),
				message: t(
					"unreadMessageMessage",
					'{{sender}} wrote a message in the channel "{{channel}}" regarding "{{subject}}":\n\n"{{message}}"\n\nClick here to read and reply: {{url}}',
					{
						lng: lang,
						sender: comment.sender.name,
						channel: channel.name,
						subject: channel.subject.name,
						message: comment.text,
						url: `${window.location.origin}?discussion=${channel.id}`,
					},
				),
				sendOnStage: true,
			};
		});
	};

	useEffect(() => {
		if (channelId) {
			updateDocument(db.collection("discussionChannels").doc(channelId), {
				seenByUserIds: firebase.firestore.FieldValue.arrayUnion(user.id),
			});
		}
	}, [channelId, user.id]);

	useEffect(() => {
		if (channel && !channel.userIds?.includes(user.id) && !hasRight("discussions.manage")) {
			setDiscussion({ channelId: null });
		}
	}, [channel, hasRight, user.id]);

	const channels = [channelId, ...(channel?.channels || [])];

	const onSetNeedsAttention = (comment) => {
		return updateDocument(comment.ref, {
			needsAttention: !comment.needsAttention,
			producerId: channel.producerId || orgId,
		});
	};

	return (
		<LoadingContext data={channel} loading={loading} error={error}>
			<DiscussionChannelHeader channel={channel} />

			<Divider style={{ margin: 0, borderBottom: "none" }} />

			<div style={{ padding: 15, overflow: "auto", maxHeight: "70vh", scrollbarWidth: "thin" }}>
				<DiscussionThread
					onSetNeedsAttention={hasRight("discussions.manage") ? onSetNeedsAttention : undefined}
					channels={channels}
					onCommentCreated={onCommentCreated}
				/>
			</div>
		</LoadingContext>
	);
}
